import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { RealtimeAnswer, RealtimeSession } from '@crowdex/provider-contracts/realtime-call';
import type { RealtimePipelineModels } from '@crowdex/provider-contracts/realtime-pipeline';
import type { VoiceTransports } from './use-voice-input';

/**
 * An operation of the user's provider of a contract, through the core; for a consumer (`live`,
 * `dictation`), with its provider and voice (Settings › Voice).
 */
export function callProvider<T>(
  contract: string,
  operation: string,
  input: unknown,
  consumer?: 'live' | 'dictation',
  /** A live call's persona: what it is told, and its voice (the selected one's otherwise). */
  persona?: string | null,
): Promise<T> {
  return apiFetch<T>(
    `/api/providers/${encodeURIComponent(contract)}/${encodeURIComponent(operation)}`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        input,
        ...(consumer === undefined ? {} : { consumer }),
        ...(persona ? { persona } : {}),
      }),
    },
  );
}

/** A voice call's signaling, by the consumer's realtime provider (dictation's). */
export function realtimeSignaling(
  consumer: 'live' | 'dictation',
  persona?: string | null,
): (sdp: string, session: RealtimeSession) => Promise<RealtimeAnswer> {
  return (sdp, session) =>
    callProvider('realtime.v1', 'call.create', { sdp, session }, consumer, persona);
}

/** The user's models, for a voice call on the core's pipeline (it answers and speaks with them). */
export const pipelineModels: RealtimePipelineModels = {
  generate: async ({ signal, ...input }) =>
    (
      await apiFetch<{ text: string }>('/api/providers/inference.v1/text.generate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ input: { ...input, maxOutputTokens: 400, timeoutMs: 30_000 } }),
        signal,
      })
    ).text,
  speak: ({ signal, ...input }) =>
    apiFetch<{ audio: string; mimeType: string }>('/api/providers/inference.v1/speech', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ input }),
      signal,
    }),
};

/** Dictation's transport: the page runs the voice call; the user's realtime provider signs it. */
export const coreVoiceTransports: VoiceTransports = {
  realtimeSignaling: realtimeSignaling('dictation'),
};
